import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Gamepad2 } from "lucide-react";

export default function Auth({ mode = "login" }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [emailRaw, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agree) { toast.error("Please confirm you are 18+ and agree to the policies"); return; }
    setBusy(true);
    const email = emailRaw.trim().toLowerCase();
    try {
      if (mode === "login") { const u = await login(email, password); nav(u.onboarded ? "/home" : "/onboarding"); }
      else if (mode === "register") { const u = await register({ email, password, display_name: name, agreed_terms: true, agreed_age: true }); nav("/onboarding"); }
      else if (mode === "forgot") { await api.post("/auth/forgot-password", { email }); toast.success("If the account exists, a reset link was sent (check server logs in dev)."); }
      else if (mode === "reset") { await api.post("/auth/reset-password", { token: sp.get("token"), password }); toast.success("Password reset. Please log in."); nav("/login"); }
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  const titles = { login: "Welcome back", register: "Create your VStar", forgot: "Reset your password", reset: "Set a new password" };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-5 relative overflow-hidden starfield">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(0,110,150,0.25),transparent_55%),radial-gradient(ellipse_at_70%_100%,rgba(212,175,55,0.12),transparent_45%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8"><Link to="/"><img src="/virtustars-logo.png" alt="" className="w-20 mx-auto mb-4" /></Link>
          <h1 className="font-display font-bold text-2xl">{titles[mode]}</h1>
          <p className="text-sm text-muted-foreground mt-1">Your Avatar. Your Career. Your Industry.</p>
        </div>
        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
          {mode === "register" && (
            <Field icon={User}><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" required className="pl-10 h-12 bg-transparent border-white/10" data-testid="auth-name-input" /></Field>
          )}
          {(mode === "login" || mode === "register" || mode === "forgot") && (
            <Field icon={Mail}><Input type="email" value={emailRaw} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className="pl-10 h-12 bg-transparent border-white/10" data-testid="auth-email-input" /></Field>
          )}
          {(mode === "login" || mode === "register" || mode === "reset") && (
            <Field icon={Lock}><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required minLength={6} className="pl-10 h-12 bg-transparent border-white/10" data-testid="auth-password-input" /></Field>
          )}
          {mode === "register" && (
            <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer select-none">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[var(--eblue)] shrink-0" data-testid="auth-agree-checkbox" />
              <span>I confirm I am <span className="text-white">18 years or older</span> and I agree to the{" "}
                <Link to="/legal/terms" target="_blank" className="text-eblue hover:underline">Terms of Service</Link>,{" "}
                <Link to="/legal/privacy" target="_blank" className="text-eblue hover:underline">Privacy Policy</Link> and{" "}
                <Link to="/legal/data" target="_blank" className="text-eblue hover:underline">Data Policy</Link>.</span>
            </label>
          )}
          {mode === "login" && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="auth-persistent-note">
              <span className="text-eblue">✓</span> You'll stay signed in on this device until you log out
            </p>
          )}
          <Button type="submit" disabled={busy || (mode === "register" && !agree)} className="btn-gold w-full h-12 rounded-xl" data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Enter VirtuStars" : mode === "register" ? "Create Your VStar" : mode === "forgot" ? "Send reset link" : "Reset password"}
            {!busy && <ArrowRight size={18} className="ml-1" />}
          </Button>
          {mode === "login" && <div className="flex justify-between text-sm"><Link to="/forgot" className="text-muted-foreground hover:text-eblue">Forgot password?</Link><Link to="/register" className="text-eblue" data-testid="auth-goto-register">Create account</Link></div>}
          {mode === "register" && <div className="text-center text-sm text-muted-foreground">Already have an account? <Link to="/login" className="text-eblue" data-testid="auth-goto-login">Log in</Link></div>}
          {(mode === "forgot" || mode === "reset") && <div className="text-center text-sm"><Link to="/login" className="text-eblue">Back to login</Link></div>}
          {(mode === "login" || mode === "register") && (
            <Link to="/fivem" className="flex items-center justify-center gap-2 text-sm surface-2 rounded-xl h-11 border border-gold/20 text-gold hover:bg-gold/10 transition" data-testid="auth-fivem-tab">
              <Gamepad2 size={16} /> FiveM Server Owners — Get the VStars App
            </Link>
          )}
        </form>
        <p className="text-center text-xs text-muted-foreground mt-4">An ImJove Tech Platform</p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, children }) {
  return <div className="relative"><Icon size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />{children}</div>;
}
